import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import * as controllers from 'controllers'
import { CompanyDto, CompanyRatesDto, companyRatesValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import { ExplanationAccordion } from 'components'
import { Junction } from 'localConstants'

interface RatesAddEditDialogProps {
  initialValues: CompanyRatesDto
  loading: boolean
  onClose: () => void
  onSave: (values: any) => void
  open: boolean
}

export default function RatesAddEditDialog({ initialValues, loading, onClose, onSave, open }: RatesAddEditDialogProps) {
  const [exporterBuyerCompanies, setExporterBuyerCompanies] = useState<CompanyDto[]>([])
  // This is used for when a user selects a company to copy rates from
  const [isGettingCompanyRates, setIsGettingCompanyRates] = useState<boolean>(false)

  useEffect(() => {
    controllers
      .getCompanies({
        page: 0,
        pageSize: 2000,
        order: 'asc',
        orderBy: 'name',
        statuses: ['isActive', 'hasRates'],
        junction: Junction.AND
      })
      .then(response => {
        setExporterBuyerCompanies(response.value)
      })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={companyRatesValidationSchema}
        onSubmit={(values, formickHelpers) => {
          onSave(values)
          formickHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, setValues, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Rates</DialogTitle>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.copyRatesFromAnotherCompany}
                          disabled={loading || isSubmitting}
                          name='copyRatesFromAnotherCompany'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' copyRatesFromAnotherCompany: "copyRatesFromAnotherCompany must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('copyRatesFromAnotherCompany', e.target.checked)

                            if (!e.target.checked) {
                              setFieldValue('company', undefined)
                            }
                          }}
                          value={values.copyRatesFromAnotherCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Copy rates from another company?</Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  {values.copyRatesFromAnotherCompany && (
                    <Grid item xs={12}>
                      <Autocomplete
                        autoHighlight
                        autoSelect
                        disabled={loading || isSubmitting /* || !values.copyRatesFromAnotherCompany*/}
                        disableListWrap
                        getOptionLabel={company => company.name!}
                        onBlur={handleBlur}
                        onChange={(_e, value) => {
                          setFieldValue('company', value)

                          if (value) {
                            setIsGettingCompanyRates(true)
                            controllers
                              .getCompanyRates(value.id!)
                              .then(response => {
                                setValues({
                                  ratesImportCharge: response.value.ratesImportCharge ?? undefined,
                                  ratesIncomingTransportation: response.value.ratesIncomingTransportation ?? undefined,
                                  ratesClusterConversion: response.value.ratesClusterConversion ?? undefined,
                                  ratesClusterProgramming: response.value.ratesClusterProgramming ?? undefined,
                                  ratesRecallExpenses: response.value.ratesRecallExpenses ?? undefined,
                                  ratesTitleFees: response.value.ratesTitleFees ?? undefined,
                                  ratesOutboundTransportation: response.value.ratesOutboundTransportation ?? undefined,
                                  copyRatesFromAnotherCompany: true,
                                  company: value
                                })
                              })
                              .finally(() => {
                                setIsGettingCompanyRates(false)
                              })
                          }
                        }}
                        options={exporterBuyerCompanies.map(exporterBuyerCompany => ({
                          id: exporterBuyerCompany.companyGuid,
                          name: exporterBuyerCompany.name
                        }))}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={Boolean(touched.company && errors.company)}
                            helperText={touched.company && errors.company}
                            label='Company to copy rates from'
                          />
                        )}
                        value={values.company ? { id: values.company.companyGuid, name: values.company.name } : null}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesImportCharge && errors.ratesImportCharge)}
                      helperText={touched.ratesImportCharge && errors.ratesImportCharge}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined
                      }}
                      label='Import Charge'
                      name='ratesImportCharge'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesImportCharge || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesIncomingTransportation && errors.ratesIncomingTransportation)}
                      helperText={touched.ratesIncomingTransportation && errors.ratesIncomingTransportation}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined
                      }}
                      label='Incoming Transportation'
                      name='ratesIncomingTransportation'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesIncomingTransportation || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesClusterConversion && errors.ratesClusterConversion)}
                      helperText={touched.ratesClusterConversion && errors.ratesClusterConversion}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined
                      }}
                      label='Cluster Conversion'
                      name='ratesClusterConversion'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesClusterConversion || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesClusterProgramming && errors.ratesClusterProgramming)}
                      helperText={touched.ratesClusterProgramming && errors.ratesClusterProgramming}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined
                      }}
                      label='Cluster Programming'
                      name='ratesClusterProgramming'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesClusterProgramming || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesRecallExpenses && errors.ratesRecallExpenses)}
                      helperText={touched.ratesRecallExpenses && errors.ratesRecallExpenses}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined
                      }}
                      label='Recall Expenses'
                      name='ratesRecallExpenses'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesRecallExpenses || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesTitleFees && errors.ratesTitleFees)}
                      helperText={touched.ratesTitleFees && errors.ratesTitleFees}
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined
                      }}
                      label='Title Fees'
                      name='ratesTitleFees'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesTitleFees || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.ratesOutboundTransportation && errors.ratesOutboundTransportation)}
                      helperText={touched.ratesOutboundTransportation && errors.ratesOutboundTransportation}
                      InputProps={{
                        endAdornment:
                          values.copyRatesFromAnotherCompany && isGettingCompanyRates ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={25} />
                            </InputAdornment>
                          ) : undefined,
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>
                      }}
                      label='Outbound Transportation'
                      name='ratesOutboundTransportation'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.ratesOutboundTransportation || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      ||All fields are required.|| All currency fields need to be in 0, 0.0 or 0.00 format.|| Selecting a
                      Company in the Company to copy rates from autocomplete populates all the text fields with the values from
                      the selected company. || Only active companies with existing rates show in the Company to copy rates from
                      autocomplete. || If copying rates from another company, the rate values are copied, not synced. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={loading || isSubmitting || isGettingCompanyRates}
                  variant='text'
                  color='inherit'
                  onClick={onClose}
                >
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting || isGettingCompanyRates} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
